/** localStorage draft for /survey/prevbrandanswer. */
export const PREV_BRAND_ANSWER_STORAGE_KEY = 'ldsurvey:prevBrandAnswer';

export type ElaboratedReason = {
  checked: boolean;
  elaboration: string;
};

export const PREV_BRAND_REASON_KEYS = [
  'taste',
  'brandImage',
  'productQuality',
  'price',
  'productFeatures',
  'recommendedByOthers',
] as const;

export type PrevBrandReasonKey = (typeof PREV_BRAND_REASON_KEYS)[number];

export type PrevBrandAnswerAnswers = {
  [K in PrevBrandReasonKey]: ElaboratedReason;
} & {
  otherReason: string;
  smokedOtherProduct: '' | 'yes' | 'no';
};

const emptyElaborated = (): ElaboratedReason => ({ checked: false, elaboration: '' });

export const emptyPrevBrandAnswerAnswers = (): PrevBrandAnswerAnswers => ({
  taste: emptyElaborated(),
  brandImage: emptyElaborated(),
  productQuality: emptyElaborated(),
  price: emptyElaborated(),
  productFeatures: emptyElaborated(),
  recommendedByOthers: emptyElaborated(),
  otherReason: '',
  smokedOtherProduct: '',
});

function parseElaborated(value: unknown): ElaboratedReason {
  if (value === true) return { checked: true, elaboration: '' };
  if (value === false || value == null) return emptyElaborated();
  if (typeof value !== 'object') return emptyElaborated();
  const o = value as Record<string, unknown>;
  return {
    checked: o.checked === true,
    elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
  };
}

export function readPrevBrandAnswerAnswers(): PrevBrandAnswerAnswers {
  try {
    const raw = localStorage.getItem(PREV_BRAND_ANSWER_STORAGE_KEY);
    if (!raw) return emptyPrevBrandAnswerAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyPrevBrandAnswerAnswers();
    const o = parsed as Record<string, unknown>;
    const smoked =
      o.smokedOtherProduct === 'yes' || o.smokedOtherProduct === 'no' ? o.smokedOtherProduct : '';
    return {
      taste: parseElaborated(o.taste),
      brandImage: parseElaborated(o.brandImage),
      productQuality: parseElaborated(o.productQuality),
      price: parseElaborated(o.price),
      productFeatures: parseElaborated(o.productFeatures),
      recommendedByOthers: parseElaborated(o.recommendedByOthers),
      otherReason: typeof o.otherReason === 'string' ? o.otherReason : '',
      smokedOtherProduct: smoked,
    };
  } catch {
    return emptyPrevBrandAnswerAnswers();
  }
}

export function writePrevBrandAnswerAnswers(data: PrevBrandAnswerAnswers): void {
  try {
    localStorage.setItem(PREV_BRAND_ANSWER_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearPrevBrandAnswerAnswers(): void {
  try {
    localStorage.removeItem(PREV_BRAND_ANSWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type PrevBrandReasonType = PrevBrandReasonKey | 'other';

export type PrevBrandReasonSelection = {
  type: PrevBrandReasonType;
  elaboration: string;
};

function hasAnyReasonSelected(data: PrevBrandAnswerAnswers): boolean {
  return PREV_BRAND_REASON_KEYS.some((key) => data[key].checked);
}

export function isPrevBrandAnswerComplete(): boolean {
  const data = readPrevBrandAnswerAnswers();
  if (!hasAnyReasonSelected(data)) return false;
  if (!data.otherReason.trim()) return false;
  return data.smokedOtherProduct === 'yes' || data.smokedOtherProduct === 'no';
}

export function getSelectedPrevBrandReasons(
  data: PrevBrandAnswerAnswers
): PrevBrandReasonSelection[] {
  const selected: PrevBrandReasonSelection[] = [];
  for (const key of PREV_BRAND_REASON_KEYS) {
    if (data[key].checked) {
      selected.push({ type: key, elaboration: data[key].elaboration });
    }
  }
  return selected;
}

export function getPrevBrandReasonDetail(data: PrevBrandAnswerAnswers): string {
  return data.otherReason.trim();
}
